import {useEffect, useState} from "react";
import {groupApi, userApi} from "@core/api";
import {Group, User} from "@core/model";
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView";
import {TreeItem} from "@mui/x-tree-view/TreeItem";
import {
  Avatar,
  Box,
  ButtonBase,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import {acronym, getUserAvatarColor, translate} from "@util";
import ArrowIcon from "@mui/icons-material/ArrowDropDown";
import {GroupIcon, UserIcon} from "@components/icons";
import {useThemeContext} from "@theme";
import {useUsers} from "@hooks";

type Props = {
  selection?: {id: string; type: "USER" | "GROUP"};
  onSelect: (v: User | Group, type: "USER" | "GROUP") => void;
};

const UserItem: React.FC<{
  u: User;
  darkMode: boolean;
  onSelect: (u: User) => void;
}> = ({u, darkMode, onSelect}) => {
  let userText;
  if (u.name && u.surname) {
    userText = `${u.name} ${u.surname}`;
  } else {
    userText = u.username;
  }

  return (
    <ButtonBase
      onClick={() => onSelect(u)}
      sx={{display: "flex", width: "100%", justifyContent: "start"}}>
      <Stack direction="row" alignItems="center" gap={1} margin={1}>
        <Avatar
          sx={{
            bgcolor: getUserAvatarColor(u, darkMode),
            width: 32,
            height: 32,
          }}>
          <Typography variant="body2">{acronym(userText)}</Typography>
        </Avatar>
        <Stack alignItems="start">
          <Typography variant="body2">{userText}</Typography>
          <Typography variant="caption">
            {translate(`common.role.${u.role}`)}
          </Typography>
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

const GroupItem: React.FC<{
  g: Group;
  onSelect: (g: Group | User, type: "USER" | "GROUP") => void;
}> = ({g, onSelect}) => {
  return (
    <TreeItem
      itemId={g.id!}
      label={<Typography variant="body2">{g.name}</Typography>}
      onClick={e => {
        e.stopPropagation();
        onSelect(g, "GROUP");
      }}>
      {g.children?.map(g => (
        <GroupItem key={g.id} g={g} onSelect={(g, type) => onSelect(g, type)} />
      ))}
    </TreeItem>
  );
};

function UserGroupSelector(props: Props) {
  const {selection, onSelect} = props;
  const {getUserFullname} = useUsers();

  const {data: groupsData} = groupApi.useFindEntity(`page=0&size=999`);
  const {data: usersData} = userApi.useFindEntity(`page=0&size=999`);

  const [selected, setSelected] = useState<{
    v: User | Group;
    type: "USER" | "GROUP";
  }>();
  const [tab, setTab] = useState<"USER" | "GROUP">("GROUP");
  const [usersGroups, setUsersGroups] = useState<Group[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const buildGroupTree = (groups: Group[], users: User[]): Group[] => {
    const groupMap = new Map<string, Group>();

    // Inizializza ogni gruppo come nodo
    groups.forEach(group => {
      groupMap.set(group.id!, {
        id: group.id!,
        name: group.name,
        parentId: group.parentId,
        children: [],
        users: users.filter(u =>
          u.groupIds ? u.groupIds.indexOf(group.id!) !== -1 : false,
        ),
      });
    });

    const tree: Group[] = [];

    // Costruisci la struttura ad albero
    groups.forEach(group => {
      if (group.parentId) {
        const parent = groupMap.get(group.parentId);
        const current = groupMap.get(group.id!);
        if (parent && current) {
          parent.children!.push(current);
        }
      } else {
        tree.push(groupMap.get(group.id!)!);
      }
    });

    return tree;
  };

  useEffect(() => {
    if (selection) {
      let v;
      if (selection.type === "USER") {
        v = usersData?.content.find(u => u.id === selection.id);
      } else {
        v = groupsData?.content.find(g => g.id === selection.id);
      }

      if (v) {
        setSelected({v: v, type: selection.type});
      }
    }
  }, [selection, usersData, groupsData]);

  useEffect(() => {
    const groups = JSON.parse(
      JSON.stringify((groupsData?.content as Group[]) ?? []),
    ) as Group[];
    const users = usersData?.content ?? [];
    const usersGroups = buildGroupTree(groups, users);
    setUsersGroups(usersGroups);
    setExpandedItems(groups.map(g => g.id!));
  }, [groupsData, usersData]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (v: Group | User, type: "USER" | "GROUP") => {
    setSelected({v, type});
    onSelect(v, type);
    setAnchorEl(null);
  };

  const getTabStyle = (t: "USER" | "GROUP") => {
    let style = {flex: 1, justifyContent: "center", p: 1};
    const selectedStyle = {borderBottom: "1px solid", fontWeight: "bold"};

    if (tab === t) {
      style = {...style, ...selectedStyle};
    }

    return style;
  };

  const {theme} = useThemeContext();
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Box>
      <ButtonBase onClick={handleClick}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          p={1}
          width={200}>
          {!selected && (
            <Typography variant="body2">Seleziona gruppo/utente</Typography>
          )}
          {selected && (
            <Stack direction="row" gap={1}>
              {selected.type === "GROUP" ? (
                <>
                  <GroupIcon color="secondary" />
                  <Typography>{selected.v.name}</Typography>
                </>
              ) : (
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  <UserIcon color="secondary" />
                  <Typography variant="body2">
                    {getUserFullname(selected.v.id)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
          <ArrowIcon />
        </Stack>
      </ButtonBase>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        <Box sx={{width: 200}}>
          <Stack direction="row">
            <ButtonBase
              sx={getTabStyle("GROUP")}
              onClick={() => setTab("GROUP")}>
              Groups
            </ButtonBase>
            <ButtonBase sx={getTabStyle("USER")} onClick={() => setTab("USER")}>
              Users
            </ButtonBase>
          </Stack>
          {tab === "USER" ? (
            <>
              {usersData?.content.map(u => (
                <UserItem
                  key={u.id}
                  u={u}
                  darkMode={theme.palette.mode === "dark"}
                  onSelect={u => handleSelect(u, "USER")}
                />
              ))}
            </>
          ) : (
            <SimpleTreeView
              disableSelection
              disabledItemsFocusable
              expandedItems={expandedItems}>
              {usersGroups.map(g => (
                <GroupItem key={g.id!} g={g} onSelect={handleSelect} />
              ))}
              {usersGroups.length === 0 && (
                <Box p={1}>
                  <Typography variant="body2">
                    {translate("common.table.noRowsLabel")}
                  </Typography>
                </Box>
              )}
            </SimpleTreeView>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

export {UserGroupSelector};
